/**
 * F2 — Projection LIVE protocol (presenter → projector), pure.
 *
 * Versioned JSON messages that travel over an already established channel (WebRTC
 * DataChannel over LAN or same-machine BroadcastChannel). The PRESENTER is the
 * only authority: it pushes the CURRENT slide COMPLETE (text included) and the
 * projector only renders — a projector needs no account, no SQLite, no
 * capability at all: it receives exactly what the director (authorized by the
 * permission engine on THEIR device) decides to project.
 *
 * PRIVACY: the `slide` payload does NOT include the presenter's notes
 * (`projection_item.notes`) nor anything outside the visible slide.
 *
 * Everything received is VALIDATED (garbage/unknown version → `null` → discard
 * without breaking), same as the P2P handoff protocol.
 */

import { isRecord } from "../p2p/guards";
import { type PresenterState, type ProjectionItemRow, splitSlides } from "./slides";

export const PROJECTION_LIVE_VERSION = 1;

/** Live slide: EVERYTHING the projector needs to paint. */
export interface LiveSlide {
  /** Item kind (song/verse/theme/announcement) — decides the style. */
  kind: string;
  /** Item title (song name, verse reference…). */
  itemTitle: string;
  /** Text of the current slide (stanza/verse/point). */
  body: string;
  slideIndex: number;
  slideCount: number;
  itemIndex: number;
  itemCount: number;
}

export type ProjectionLiveMessage =
  | {
      v: typeof PROJECTION_LIVE_VERSION;
      type: "hello";
      role: "presenter" | "projector";
    }
  | { v: typeof PROJECTION_LIVE_VERSION; type: "slide"; slide: LiveSlide }
  | { v: typeof PROJECTION_LIVE_VERSION; type: "blank" }
  | { v: typeof PROJECTION_LIVE_VERSION; type: "bye" };

export function encodeLiveMessage(message: ProjectionLiveMessage): string {
  return JSON.stringify(message);
}

function parseSlide(value: unknown): LiveSlide | null {
  if (!isRecord(value)) {
    return null;
  }
  if (
    typeof value.kind !== "string" ||
    typeof value.itemTitle !== "string" ||
    typeof value.body !== "string" ||
    typeof value.slideIndex !== "number" ||
    typeof value.slideCount !== "number" ||
    typeof value.itemIndex !== "number" ||
    typeof value.itemCount !== "number"
  ) {
    return null;
  }
  return {
    kind: value.kind,
    itemTitle: value.itemTitle,
    body: value.body,
    slideIndex: value.slideIndex,
    slideCount: value.slideCount,
    itemIndex: value.itemIndex,
    itemCount: value.itemCount,
  };
}

/** Garbage, unknown version or invalid shape → `null` (discard). */
export function parseLiveMessage(raw: string): ProjectionLiveMessage | null {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(value) || value.v !== PROJECTION_LIVE_VERSION) {
    return null;
  }
  switch (value.type) {
    case "hello": {
      if (value.role === "presenter" || value.role === "projector") {
        return { v: PROJECTION_LIVE_VERSION, type: "hello", role: value.role };
      }
      return null;
    }
    case "slide": {
      const slide = parseSlide(value.slide);
      return slide ? { v: PROJECTION_LIVE_VERSION, type: "slide", slide } : null;
    }
    case "blank": {
      return { v: PROJECTION_LIVE_VERSION, type: "blank" };
    }
    case "bye": {
      return { v: PROJECTION_LIVE_VERSION, type: "bye" };
    }
    default: {
      return null;
    }
  }
}

/**
 * Builds the CURRENT slide payload from the presenter's
 * state. Returns `null` if there is nothing to project. The presenter's NOTES
 * are left out ON PURPOSE.
 */
export function buildLiveSlide(
  items: readonly Pick<ProjectionItemRow, "kind" | "title" | "content">[],
  state: PresenterState,
): LiveSlide | null {
  const item = items[state.itemIndex];
  if (!item) {
    return null;
  }
  const slides = splitSlides(item);
  const body = slides[state.slideIndex];
  if (body === undefined) {
    return null;
  }
  return {
    kind: item.kind ?? "theme",
    itemTitle: item.title ?? "",
    body,
    slideIndex: state.slideIndex,
    slideCount: slides.length,
    itemIndex: state.itemIndex,
    itemCount: items.length,
  };
}
